/* Store-specific scripts, loaded after theme.js */

/* Product page colour swatches scroll sideways on phones (see custom.css);
   bring the selected swatch into view so shoppers can see which colour they're on */
(() => {
  const mobile = window.matchMedia('(max-width: 767px)');

  const revealSelected = () => {
    if (!mobile.matches) return;

    document.querySelectorAll('.product__info .swatches').forEach((row) => {
      const selected = row.querySelector('input:checked')?.closest('li');
      if (!selected || row.scrollWidth <= row.clientWidth) return;

      // Leave one swatch of the previous colour visible so the row reads as scrollable both ways
      const offset = selected.getBoundingClientRect().left - row.getBoundingClientRect().left;
      const target = row.scrollLeft + offset - selected.offsetWidth;
      row.scrollLeft = Math.max(0, target);
    });
  };

  document.addEventListener('DOMContentLoaded', revealSelected);
  document.addEventListener('variant:change', () => requestAnimationFrame(revealSelected));
  mobile.addEventListener('change', revealSelected);
})();

/* Appstle subscription widget: render its prices with the same smaller pence as the theme
   (matches the markup from snippets/price-split.liquid). The widget redraws itself, so watch for changes. */
(() => {
  const SELECTOR = '.appstle_sub_widget :is(.appstle_subscription_amount, .appstle_subscription_compare_amount)';

  const split = (el) => {
    if (el.querySelector('.price-split')) return;
    const text = el.textContent.trim();
    const match = text.match(/^(\D*?)([\d,]+)\.(\d{2})(.*)$/);
    if (!match) return;
    const [, symbol, major, minor, suffix] = match;
    el.innerHTML =
      '<span class="price-split">' +
      (symbol ? `<span class="price-split__symbol">${symbol}</span>` : '') +
      `<span class="price-split__major">${major}</span>` +
      '<span class="price-split__decimal">.</span>' +
      `<span class="price-split__minor">${minor}${suffix}</span>` +
      '</span>';
  };

  // Appstle's subscription price inside the product price block: split it and give it the theme's price styling
  const splitFinalPrice = (el) => {
    if (el.querySelector('.price-split')) return;
    split(el);
    if (el.querySelector('.price-split')) {
      el.innerHTML = `<span class="price__regular whitespace-nowrap">${el.innerHTML}</span>`;
    }
  };

  const PRICE_SELECTOR = `${SELECTOR}, .appstle_subscription_final_price`;
  const pendingPrices = new Set();
  let frame = null;

  const queuePrice = (price) => {
    pendingPrices.add(price);
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      pendingPrices.forEach((el) => {
        if (!el.isConnected) return;
        if (el.matches('.appstle_subscription_final_price')) splitFinalPrice(el);
        else split(el);
      });
      pendingPrices.clear();
    });
  };

  const scanPrices = (root) => {
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    if (root.matches(PRICE_SELECTOR)) queuePrice(root);
    root.querySelectorAll(PRICE_SELECTOR).forEach(queuePrice);
  };

  const start = () => {
    scanPrices(document.body);
    new MutationObserver((records) => {
      records.forEach((record) => {
        const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
        const price = target?.closest(PRICE_SELECTOR);
        if (price) queuePrice(price);
        record.addedNodes.forEach(scanPrices);
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* Newsletter popup: Shopify marks every sign-up form on the page as "posted successfully",
   so after a footer sign-up the popup would also open and reveal the discount code.
   Only keep the popup's success message when the popup's own form was submitted
   (Shopify returns to the page with the submitted form's id in the URL hash). */
(() => {
  const popupForm = document.querySelector('#NewsletterModal form');
  if (!popupForm) return;
  const success = popupForm.querySelector('.alert--success');
  if (success && window.location.hash !== `#${popupForm.id}`) success.remove();
})();

/* Collections (mobile/tablet): the floating "Filter and sort" button covers the bottom of the
   product grid. Hide it while scrolling down, bring it back when scrolling up. The theme's own
   logic (only show within the product grid) still applies on top of this. */
(() => {
  const sticky = document.querySelector('facet-sticky');
  if (!sticky) return;

  let lastY = window.scrollY;
  let ticking = false;

  const update = () => {
    const y = window.scrollY;
    const delta = y - lastY;
    if (Math.abs(delta) > 8) {
      sticky.classList.toggle('is-scrolling-down', delta > 0 && y > 100);
      lastY = y;
    }
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
})();

/* Product cards: show the Yotpo star rating on the card image. Shopify's review metafields are
   only set on a couple of products, so snippets/product-card.liquid outputs a hidden badge and we
   fill it from Yotpo's public bottomline (cached per product), including cards loaded later by
   filters, pagination or recommendations. */
(() => {
  const script = [...document.scripts].find((s) => /staticw2\.yotpo\.com\/[^/]+\/widget\.js/.test(s.src));
  const appKey = script ? script.src.match(/yotpo\.com\/([^/]+)\/widget\.js/)[1] : 'KOt1DjRDqp2miWYX8Ar3V4j6uZb0FteSls2vNZ2M';
  const cache = new Map();

  const bottomline = (id) => {
    if (!cache.has(id)) {
      cache.set(id, fetch(`https://api-cdn.yotpo.com/v1/widget/${appKey}/products/${id}/reviews.json?per_page=1&page=1`)
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => json?.response?.bottomline || null)
        .catch(() => null));
    }
    return cache.get(id);
  };

  const fill = (badge) => {
    if (badge.dataset.yotpoRatingDone) return;
    badge.dataset.yotpoRatingDone = '1';
    bottomline(badge.dataset.yotpoRating).then((b) => {
      if (!b || !b.total_review) return;
      const score = Math.round(b.average_score * 10) / 10;
      badge.querySelector('[data-yotpo-rating-value]').textContent = score.toFixed(1);
      badge.querySelector('[data-yotpo-rating-label]').setAttribute('aria-label', `${score} out of 5 stars`);
      badge.title = `${b.total_review} review${b.total_review === 1 ? '' : 's'}`;
      badge.hidden = false;
    });
  };

  // Observe the card, not the hidden rating badge, so it can intersect before the rating is loaded.
  const targets = new Map();
  const badgeTargets = new WeakMap();
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      targets.get(entry.target)?.forEach((badge) => {
        badgeTargets.delete(badge);
        if (badge.isConnected) fill(badge);
      });
      targets.delete(entry.target);
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '300px 0px' }) : null;

  const watch = (badge) => {
    if (badge.dataset.yotpoRatingDone || badgeTargets.has(badge)) return;
    if (!observer) return fill(badge);
    const target = badge.closest('.product-card') || badge.parentElement;
    if (!target) return;
    if (!targets.has(target)) targets.set(target, new Set());
    targets.get(target).add(badge);
    badgeTargets.set(badge, target);
    observer.observe(target);
  };

  const unwatch = (badge) => {
    const target = badgeTargets.get(badge);
    if (!target) return;
    const badges = targets.get(target);
    badges?.delete(badge);
    badgeTargets.delete(badge);
    if (!badges?.size) {
      targets.delete(target);
      observer?.unobserve(target);
    }
  };

  const scan = (root, callback) => {
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    if (root.matches('[data-yotpo-rating]')) callback(root);
    root.querySelectorAll('[data-yotpo-rating]').forEach(callback);
  };

  const start = () => {
    scan(document.body, watch);
    new MutationObserver((records) => {
      // Process removals first so moved or reinserted cards can be observed again.
      records.forEach((record) => record.removedNodes.forEach((node) => scan(node, unwatch)));
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.isConnected) scan(node, watch);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* Product page upsell carousel (snippets/product-upsell.liquid): reuse the theme's complementary
   products carousel, which otherwise looks for a <product-recommendations> wrapper */
(() => {
  const ProductComplementary = customElements.get('product-complementary');
  if (!ProductComplementary || customElements.get('product-upsell')) return;

  class ProductUpsell extends ProductComplementary {
    get container() {
      return this.closest('.product-upsell');
    }
  }

  customElements.define('product-upsell', ProductUpsell);
})();

/* Corner Cart (app) is the cart drawer: it opens on add to cart and holds the free gifts.
   Send the theme's cart buttons to it too, so there's only one cart. Falls back to the theme drawer
   if the app isn't loaded. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('.cart-drawer-button, [aria-controls^="MiniCart"], [aria-controls^="CartDrawer"]');
  if (!trigger || !window.corner || typeof window.corner.do !== 'function') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.corner.do('openCart');
}, true);

/* Appstle subscriptions: the Subscribe & Save widget sits outside the product form, so its selling_plan
   radios never reach /cart/add and subscriptions were added as one-time purchases. Copy the chosen plan
   into a hidden field in each add to cart form in the same section (left disabled for one-time purchase). */
(() => {
  const sync = (radio) => {
    const section = radio.closest('.shopify-section') || document;
    section.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      let input = form.querySelector('input[type="hidden"][name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }
      input.value = radio.value || '';
      input.disabled = !radio.value;
    });
  };

  const syncChecked = (root = document) => {
    root.querySelectorAll('input[name="selling_plan"]:checked').forEach((radio) => {
      if (!radio.closest('form[action*="/cart/add"]')) sync(radio);
    });
  };

  // Appstle updates the checked radio's value when the delivery frequency changes, so re-sync on any change
  document.addEventListener('change', () => syncChecked());
  document.addEventListener('click', (event) => {
    if (event.target.closest('.appstle_sub_widget')) setTimeout(syncChecked, 0);
  });
  // and just before adding to cart. Corner Cart takes over the add to cart click and reads the form fields
  // itself (the form is never submitted), so this runs on click in the capture phase, before Corner's handler
  document.addEventListener('click', (event) => {
    if (event.target.closest('form[action*="/cart/add"] [type="submit"], [type="submit"][form]')) syncChecked();
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.target.matches('form[action*="/cart/add"]')) syncChecked();
  }, true);
})();

/* Corner Cart order summary: add a Subtotal line (cart total before discounts) above Corner's Discount line,
   so Subtotal - Discount = the checkout total. Corner re-renders the summary, so this watches for changes and
   refetches the cart whenever Corner's checkout total changes. Works in the drawer and on the cart page. */
(() => {
  let lastSignature = null;
  let subtotal = null;
  let pending = null;

  const format = (cents) => '£' + (cents / 100).toFixed(2);

  const paint = () => {
    document.querySelectorAll('#corner-cowi-cart-summary-items-wrapper').forEach((wrapper) => {
      let row = wrapper.querySelector('[data-beyond-subtotal]');
      if (subtotal === null) {
        if (row) row.remove();
        return;
      }
      if (!row) {
        row = document.createElement('div');
        row.setAttribute('data-beyond-subtotal', '');
        row.className = 'px-5 pb-1 flex items-center justify-between';
        row.innerHTML = '<span class="text-sm text-cowi-secondary-type">Subtotal</span><span class="text-sm font-bold text-cowi-primary-type"></span>';
        wrapper.prepend(row);
      }
      const value = row.lastElementChild;
      if (value.textContent !== format(subtotal)) value.textContent = format(subtotal);
    });
  };

  const refresh = () => {
    clearTimeout(pending);
    pending = setTimeout(() => {
      fetch(window.Shopify.routes.root + 'cart.js')
        .then((response) => response.json())
        .then((cart) => {
          subtotal = cart.item_count > 0 ? cart.original_total_price : null;
          paint();
        })
        .catch(() => {});
    }, 300);
  };

  const check = () => {
    const summary = document.getElementById('corner-cowi-cart-summary-card');
    if (!summary) return;
    const signature = summary.textContent.replace(/Subtotal£[\d.,]+/, '');
    if (signature !== lastSignature) {
      lastSignature = signature;
      refresh();
    } else {
      paint();
    }
  };

  const start = () => {
    check();
    const SUMMARY_SELECTOR = '#corner-cowi-cart-summary-card, #corner-cowi-cart-summary-items-wrapper';
    let frame = null;
    const containsSummary = (node) => node.nodeType === Node.ELEMENT_NODE &&
      (node.matches(SUMMARY_SELECTOR) || node.querySelector(SUMMARY_SELECTOR));
    const isSubtotal = (node) => node.nodeType === Node.ELEMENT_NODE && node.matches('[data-beyond-subtotal]');

    new MutationObserver((records) => {
      const relevant = records.some((record) => {
        const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
        if (target?.closest('[data-beyond-subtotal]')) return false;
        if (target?.closest(SUMMARY_SELECTOR)) {
          // Our own row insertion/removal must not trigger another cart request.
          const nodes = [...record.addedNodes, ...record.removedNodes];
          return !nodes.length || !nodes.every(isSubtotal);
        }
        return [...record.addedNodes, ...record.removedNodes].some(containsSummary);
      });
      if (!relevant || frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (!document.getElementById('corner-cowi-cart-summary-card')) {
          lastSignature = null;
          return;
        }
        check();
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
